import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Car, Mail, Lock, AlertCircle, KeyRound, ArrowLeft, Loader2, RefreshCw, MessageCircle } from 'lucide-react';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Card, { CardContent } from '../components/ui/Card';
import { useAuth } from '../context/AuthContext';
import { userAPI } from '../services/api';
import appConfig from '../config/appConfig';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:7000/api';

const Login = () => {
  const [loginMode, setLoginMode] = useState('password'); // 'password' | 'otp'
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login, loginWithOtp } = useAuth();

  // OTP states
  const [otpStep, setOtpStep] = useState('email'); // 'email' | 'otp'
  const [otpMethod, setOtpMethod] = useState('email'); // 'email' | 'whatsapp'
  const [otpEmail, setOtpEmail] = useState('');
  const [otpPhone, setOtpPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpTimer, setOtpTimer] = useState(0);

  const otpRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];

  // OTP countdown
  useEffect(() => {
    if (otpTimer <= 0) return;
    const interval = setInterval(() => setOtpTimer(prev => prev - 1), 1000);
    return () => clearInterval(interval);
  }, [otpTimer]);

  // Password login
  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(loginId, password);
      if (result.success) {
        navigate('/dashboard', { replace: true });
      } else {
        setError(result.message || 'Login failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Send OTP
  const handleSendOtp = async () => {
    const isWhatsApp = otpMethod === 'whatsapp';
    if (isWhatsApp) {
      if (otpPhone.replace(/\D/g, '').length < 10) {
        setOtpError('Please enter a valid phone number');
        return;
      }
    } else if (!otpEmail || !otpEmail.includes('@')) {
      setOtpError('Please enter a valid email address');
      return;
    }
    setOtpError('');
    setOtpLoading(true);

    try {
      const endpoint = isWhatsApp ? 'send-otp' : 'send-email-otp';
      // staffLogin: this is the management login — only existing admin/owner/staff
      // may sign in (the backend must NOT auto-create a customer).
      const payload = isWhatsApp ? { phone: otpPhone.trim(), staffLogin: true } : { email: otpEmail.trim(), staffLogin: true };
      const response = await fetch(`${API_BASE}/users/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();

      if (data.success) {
        setOtpStep('otp');
        setOtpTimer(30);
      } else {
        setOtpError(data.message || 'Failed to send OTP');
      }
    } catch (err) {
      setOtpError('Something went wrong. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  // Verify OTP
  const handleVerifyOtp = async () => {
    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      setOtpError('Please enter the 6-digit OTP');
      return;
    }
    setOtpError('');
    setOtpLoading(true);

    try {
      const response = await fetch(`${API_BASE}/users/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          otpMethod === 'whatsapp'
            ? { phone: otpPhone.trim(), otp: otpCode }
            : { email: otpEmail.trim(), otp: otpCode }
        )
      });
      const data = await response.json();

      if (data.success) {
        loginWithOtp(data.token, data.user);
        navigate('/dashboard', { replace: true });
      } else {
        setOtpError(data.message || 'Invalid OTP');
      }
    } catch (err) {
      setOtpError('Something went wrong. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    if (otpTimer > 0) return;
    setOtpError('');
    setOtpLoading(true);

    try {
      const endpoint = otpMethod === 'whatsapp' ? 'send-otp' : 'send-email-otp';
      const payload = otpMethod === 'whatsapp' ? { phone: otpPhone.trim(), staffLogin: true } : { email: otpEmail.trim(), staffLogin: true };
      const response = await fetch(`${API_BASE}/users/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.success) {
        setOtpTimer(30);
        setOtp(['', '', '', '', '', '']);
      } else {
        setOtpError(data.message || 'Failed to resend OTP');
      }
    } catch (err) {
      setOtpError('Something went wrong.');
    } finally {
      setOtpLoading(false);
    }
  };

  // OTP input handlers
  const handleOtpChange = (index, value) => {
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newOtp = [...otp];
      digits.forEach((d, i) => { if (index + i < 6) newOtp[index + i] = d; });
      setOtp(newOtp);
      const nextIdx = Math.min(index + digits.length, 5);
      otpRefs[nextIdx]?.current?.focus();
      return;
    }
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) otpRefs[index + 1].current?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs[index - 1].current?.focus();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <img src={appConfig.logo} alt="Wheels4Rent" className="w-24 h-24 object-contain mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Owner / Staff Login</h1>
          <p className="text-gray-600">Sign in to manage your {appConfig.name} business</p>
        </div>

        {/* Login Card */}
        <Card>
          <CardContent className="p-8">
            {/* Login Mode Toggle */}
            <div className="flex rounded-lg bg-gray-100 p-1 mb-6">
              <button
                type="button"
                onClick={() => { setLoginMode('password'); setError(''); setOtpError(''); setOtpStep('email'); }}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  loginMode === 'password' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Lock className="h-4 w-4" /> Password
              </button>
              <button
                type="button"
                onClick={() => { setLoginMode('otp'); setError(''); setOtpError(''); }}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  loginMode === 'otp' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <KeyRound className="h-4 w-4" /> OTP Login
              </button>
            </div>

            {/* Password Login */}
            {loginMode === 'password' && (
              <form onSubmit={handlePasswordLogin} className="space-y-6">
                <Input
                  label="Email or Phone"
                  type="text"
                  placeholder="Enter your email or phone number"
                  value={loginId}
                  onChange={(e) => setLoginId(e.target.value)}
                  autoComplete="username"
                  required
                />
                <Input
                  label="Password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <div className="flex items-center justify-between">
                  <label className="flex items-center">
                    <input type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                    <span className="ml-2 text-sm text-gray-700">Remember me</span>
                  </label>
                  <Link to="/forgot-password" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                    Forgot password?
                  </Link>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
                    <AlertCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-800 font-medium">{error}</p>
                  </div>
                )}

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Signing in...' : 'Sign in'}
                </Button>
              </form>
            )}

            {/* OTP Login */}
            {loginMode === 'otp' && (
              <div className="space-y-5">
                {otpStep === 'email' && (
                  <>
                    {/* Email / WhatsApp method toggle */}
                    <div className="flex bg-gray-100 rounded-lg p-1">
                      <button
                        type="button"
                        onClick={() => { setOtpMethod('email'); setOtpError(''); }}
                        className={`flex-1 py-2 rounded-md text-sm font-medium flex items-center justify-center gap-1 transition-colors ${otpMethod === 'email' ? 'bg-white shadow text-blue-600' : 'text-gray-500'}`}
                      >
                        <Mail className="h-4 w-4" /> Email
                      </button>
                      <button
                        type="button"
                        onClick={() => { setOtpMethod('whatsapp'); setOtpError(''); }}
                        className={`flex-1 py-2 rounded-md text-sm font-medium flex items-center justify-center gap-1 transition-colors ${otpMethod === 'whatsapp' ? 'bg-white shadow text-green-600' : 'text-gray-500'}`}
                      >
                        <MessageCircle className="h-4 w-4" /> WhatsApp
                      </button>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {otpMethod === 'whatsapp' ? 'Phone Number' : 'Email Address'}
                      </label>
                      <div className="relative">
                        {otpMethod === 'whatsapp' ? (
                          <>
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium">+91</span>
                            <input
                              type="tel"
                              value={otpPhone}
                              onChange={(e) => setOtpPhone(e.target.value)}
                              placeholder="Enter your registered phone"
                              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-900"
                              onKeyDown={(e) => e.key === 'Enter' && handleSendOtp()}
                            />
                          </>
                        ) : (
                          <>
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                              type="email"
                              value={otpEmail}
                              onChange={(e) => setOtpEmail(e.target.value)}
                              placeholder="Enter your registered email"
                              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                              onKeyDown={(e) => e.key === 'Enter' && handleSendOtp()}
                            />
                          </>
                        )}
                      </div>
                    </div>

                    {otpError && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start">
                        <AlertCircle className="h-4 w-4 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-red-800">{otpError}</p>
                      </div>
                    )}

                    <Button
                      type="button"
                      className="w-full"
                      onClick={handleSendOtp}
                      disabled={otpLoading || (otpMethod === 'whatsapp' ? otpPhone.replace(/\D/g, '').length < 10 : (!otpEmail || !otpEmail.includes('@')))}
                    >
                      {otpLoading ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending OTP...</>
                      ) : (
                        otpMethod === 'whatsapp' ? 'Send OTP on WhatsApp' : 'Send OTP'
                      )}
                    </Button>
                  </>
                )}

                {otpStep === 'otp' && (
                  <>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">
                        OTP sent to <span className="font-medium text-gray-900">{otpMethod === 'whatsapp' ? `+91 ${otpPhone}` : otpEmail}</span>
                      </p>
                      <button
                        type="button"
                        onClick={() => { setOtpStep('email'); setOtp(['', '', '', '', '', '']); setOtpError(''); }}
                        className="text-xs text-blue-600 hover:underline mt-1"
                      >
                        {otpMethod === 'whatsapp' ? 'Change number' : 'Change email'}
                      </button>
                    </div>

                    {/* OTP Input */}
                    <div className="flex justify-center gap-3">
                      {otp.map((digit, index) => (
                        <input
                          key={index}
                          ref={otpRefs[index]}
                          type="text"
                          inputMode="numeric"
                          value={digit}
                          onChange={(e) => handleOtpChange(index, e.target.value)}
                          onKeyDown={(e) => handleOtpKeyDown(index, e)}
                          className="w-11 h-12 text-center text-xl font-bold border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-gray-900"
                          maxLength={6}
                          autoFocus={index === 0}
                        />
                      ))}
                    </div>

                    {otpError && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start">
                        <AlertCircle className="h-4 w-4 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-red-800">{otpError}</p>
                      </div>
                    )}

                    <Button
                      type="button"
                      className="w-full"
                      onClick={handleVerifyOtp}
                      disabled={otpLoading || otp.join('').length !== 6}
                    >
                      {otpLoading ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Verifying...</>
                      ) : (
                        'Verify & Login'
                      )}
                    </Button>

                    <div className="text-center">
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        disabled={otpTimer > 0}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium disabled:text-gray-400 flex items-center justify-center mx-auto gap-1"
                      >
                        <RefreshCw className={`h-3.5 w-3.5 ${otpLoading ? 'animate-spin' : ''}`} />
                        {otpTimer > 0 ? `Resend in ${otpTimer}s` : 'Resend OTP'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">New to {appConfig.name}?</span>
                </div>
              </div>
              <div className="mt-6 text-center">
                <Link to="/register" className="text-blue-600 hover:text-blue-700 font-medium">
                  Create an account
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-gray-600 mt-8">
          By signing in, you agree to our{' '}
          <Link to="/terms" className="text-blue-600 hover:text-blue-700">Terms of Service</Link>
          {' '}and{' '}
          <Link to="/privacy" className="text-blue-600 hover:text-blue-700">Privacy Policy</Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
